// Google Analytics 4 Service for Stock Analysis Platform
// Handles all user tracking and custom events

class AnalyticsService {
  constructor() {
    this.GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-V62X5JB371';
    this.isInitialized = false;
    this.isDebugMode = process.env.NODE_ENV === 'development';
    
    // Only track in production unless explicitly enabled for development
    this.shouldTrack = process.env.NODE_ENV === 'production' || 
                       process.env.REACT_APP_ENABLE_ANALYTICS_DEV === 'true';
  }

  // Initialize Google Analytics 4
  init() {
    if (this.isInitialized || !this.shouldTrack || !this.GA_MEASUREMENT_ID) {
      if (!this.shouldTrack) {
        this.log('Analytics disabled for development environment');
      }
      return;
    }

    try {
      // Load gtag script
      window.dataLayer = window.dataLayer || [];
      function gtag() {
        window.dataLayer.push(arguments);
      }
      window.gtag = gtag;

      // Initialize with measurement ID
      gtag('js', new Date());
      gtag('config', this.GA_MEASUREMENT_ID, {
        // Privacy-focused configuration for financial app
        anonymize_ip: true,
        allow_google_signals: false,  // Disable ads features
        allow_ad_personalization_signals: false,
        page_title: document.title,
        page_location: window.location.href
      });

      // Load GA4 script
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${this.GA_MEASUREMENT_ID}`;
      document.head.appendChild(script);

      this.isInitialized = true;
      this.log('Analytics initialized successfully');
    } catch (error) {
      console.error('Failed to initialize analytics:', error);
    }
  }

  // Track page views
  trackPageView(pageName, additionalParams = {}) {
    if (!this.isInitialized || !this.shouldTrack) return;

    const params = {
      page_title: pageName,
      page_location: window.location.href,
      page_path: window.location.pathname,
      ...additionalParams
    };

    window.gtag('event', 'page_view', params);
    this.log('Page view tracked:', pageName, params);
  }

  // Track stock-specific events
  trackStockEvent(eventName, stockSymbol, additionalParams = {}) {
    if (!this.isInitialized || !this.shouldTrack) return;

    const params = {
      stock_symbol: stockSymbol,
      event_category: 'stock_interaction',
      ...additionalParams
    };

    window.gtag('event', eventName, params);
    this.log('Stock event tracked:', eventName, stockSymbol, params);
  }

  // Enhanced feature usage tracking with granular context
  trackFeatureUsage(featureName, action, additionalParams = {}) {
    if (!this.isInitialized || !this.shouldTrack) return;

    const params = {
      feature_name: featureName,
      action: action,
      event_category: 'feature_usage',
      // Enhanced granular parameters
      interaction_type: additionalParams.interactionType || 'click',
      feature_section: additionalParams.section || 'main',
      user_intent: additionalParams.intent || 'explore',
      session_depth: this.getSessionDepth(),
      page_context: window.location.pathname,
      feature_complexity: additionalParams.complexity || 'basic',
      user_experience_level: additionalParams.userLevel || 'intermediate',
      timestamp: new Date().toISOString(),
      ...additionalParams
    };

    window.gtag('event', 'feature_interaction', params);
    this.log('Feature usage tracked:', featureName, action, params);
  }

  // Enhanced user engagement tracking with behavioral insights
  trackEngagement(engagementType, value = null, additionalParams = {}) {
    if (!this.isInitialized || !this.shouldTrack) return;

    const params = {
      engagement_type: engagementType,
      event_category: 'user_engagement',
      // Enhanced engagement context
      engagement_quality: this.calculateEngagementQuality(value, additionalParams),
      session_feature_count: this.getSessionFeatureCount(),
      interaction_density: this.getInteractionDensity(),
      user_behavior_pattern: this.identifyBehaviorPattern(additionalParams),
      content_depth: additionalParams.contentDepth || 'surface',
      attention_span: additionalParams.attentionSpan || 'normal',
      task_completion_stage: additionalParams.completionStage || 'in_progress',
      ...additionalParams
    };

    if (value !== null) {
      params.value = value;
    }

    window.gtag('event', 'user_engagement', params);
    this.log('Engagement tracked:', engagementType, params);
  }

  // Track API calls (for performance monitoring)
  trackApiCall(endpoint, method, responseTime = null, status = null) {
    if (!this.isInitialized || !this.shouldTrack) return;

    const params = {
      api_endpoint: endpoint,
      method: method,
      event_category: 'api_performance'
    };

    if (responseTime !== null) {
      params.response_time = Math.round(responseTime);
    }

    if (status !== null) {
      params.status_code = status;
    }

    window.gtag('event', 'api_call', params);
    this.log('API call tracked:', endpoint, params);
  }

  // Track funnel progression for conversion analysis
  trackFunnelStep(funnelName, stepName, stepNumber, context = {}) {
    if (!this.isInitialized || !this.shouldTrack) return;

    const params = {
      funnel_name: funnelName,
      step_name: stepName,
      step_number: stepNumber,
      timestamp: new Date().toISOString(),
      event_category: 'funnel_progression',
      user_segment: context.userSegment || 'unknown',
      step_completion_time: context.completionTime || 0,
      previous_step: context.previousStep || '',
      drop_off_risk: context.dropOffRisk || 'low',
      funnel_session_id: context.sessionId || '',
      conversion_intent: context.conversionIntent || 'medium'
    };

    window.gtag('event', 'funnel_step_completed', params);
    this.log('Funnel step tracked:', funnelName, stepName, params);
  }

  // Track user authentication events
  trackAuth(action, method = null) {
    if (!this.isInitialized || !this.shouldTrack) return;

    const params = {
      auth_action: action,
      event_category: 'authentication'
    };

    if (method) {
      params.auth_method = method;
    }

    window.gtag('event', 'auth_interaction', params);
    this.log('Auth event tracked:', action, params);
  }

  // Track errors
  trackError(errorType, errorMessage, context = {}) {
    if (!this.isInitialized || !this.shouldTrack) return;

    const params = {
      error_type: errorType,
      error_message: errorMessage.substring(0, 150), // Limit message length
      event_category: 'errors',
      ...context
    };

    window.gtag('event', 'exception', params);
    this.log('Error tracked:', errorType, params);
  }

  // Set user properties (when user logs in)
  setUserProperties(userId, properties = {}) {
    if (!this.isInitialized || !this.shouldTrack) return;

    // Set user ID for cross-session tracking
    window.gtag('config', this.GA_MEASUREMENT_ID, {
      user_id: userId
    });

    // Set custom user properties
    Object.entries(properties).forEach(([key, value]) => {
      window.gtag('event', 'user_property', {
        custom_parameter: key,
        value: value
      });
    });

    this.log('User properties set:', userId, properties);
  }

  // Clear user data (on logout)
  clearUser() {
    if (!this.isInitialized || !this.shouldTrack) return;

    window.gtag('config', this.GA_MEASUREMENT_ID, {
      user_id: null
    });

    this.log('User data cleared');
  }

  // Debug logging
  log(...args) {
    if (this.isDebugMode) {
      console.log('[Analytics]', ...args);
    }
  }

  // Enhanced scroll tracking with content context
  trackScrollDepth(depth, contentType = 'page', contentId = null, additionalParams = {}) {
    if (!this.isInitialized || !this.shouldTrack) return;

    const params = {
      scroll_depth: depth,
      content_type: contentType, // 'page', 'chart', 'table', 'modal', 'list'
      content_id: contentId,
      page_path: window.location.pathname,
      viewport_height: window.innerHeight,
      document_height: document.documentElement.scrollHeight,
      scroll_speed: this.calculateScrollSpeed(),
      engagement_time: this.getEngagementTime(),
      scroll_direction: additionalParams.direction || 'down',
      scroll_trigger: additionalParams.trigger || 'user', // 'user', 'programmatic'
      content_consumed_percentage: Math.round((depth / 100) * 100),
      ...additionalParams
    };

    window.gtag('event', 'scroll', params);
    this.log('Scroll tracked:', depth, contentType, params);
  }

  // Set custom user properties for segmentation
  setCustomUserProperties(properties) {
    if (!this.isInitialized || !this.shouldTrack) return;

    // Standard user properties for financial app
    const userProps = {
      user_tier: properties.tier || 'free', // 'free', 'premium', 'pro'
      portfolio_size: properties.portfolioSize || 'small', // 'small', 'medium', 'large'
      trading_experience: properties.experience || 'beginner', // 'beginner', 'intermediate', 'expert'
      feature_adoption_stage: properties.adoptionStage || 'explorer', // 'explorer', 'analyzer', 'trader'
      signup_date: properties.signupDate,
      last_activity: new Date().toISOString(),
      preferred_analysis_type: properties.analysisType || 'technical', // 'technical', 'fundamental', 'mixed'
      risk_tolerance: properties.riskTolerance || 'moderate', // 'conservative', 'moderate', 'aggressive'
      ...properties
    };

    window.gtag('set', 'user_properties', userProps);
    this.log('User properties set:', userProps);
  }

  // Helper methods for enhanced analytics
  getSessionDepth() {
    if (!window.sessionStorage) return 0;
    const depth = parseInt(sessionStorage.getItem('analytics_session_depth') || '0');
    sessionStorage.setItem('analytics_session_depth', (depth + 1).toString());
    return depth + 1;
  }

  getSessionFeatureCount() {
    if (!window.sessionStorage) return 0;
    return parseInt(sessionStorage.getItem('analytics_feature_count') || '0');
  }

  incrementFeatureCount() {
    if (!window.sessionStorage) return;
    const count = this.getSessionFeatureCount();
    sessionStorage.setItem('analytics_feature_count', (count + 1).toString());
  }

  getEngagementTime() {
    const startTime = parseInt(sessionStorage.getItem('analytics_session_start') || Date.now());
    return Math.round((Date.now() - startTime) / 1000); // seconds
  }

  calculateScrollSpeed() {
    const now = Date.now();
    const lastScroll = parseInt(sessionStorage.getItem('analytics_last_scroll') || now);
    const speed = lastScroll ? Math.max(0, 1000 / (now - lastScroll)) : 0;
    sessionStorage.setItem('analytics_last_scroll', now.toString());
    return Math.round(speed * 10) / 10; // pixels per second
  }

  calculateEngagementQuality(duration, context) {
    if (!duration) return 'unknown';
    
    const featureCount = this.getSessionFeatureCount();
    
    // Quality based on time and interaction density
    if (duration > 60 && featureCount > 3) return 'high';
    if (duration > 30 && featureCount > 1) return 'medium';
    if (duration > 10) return 'low';
    return 'bounce';
  }

  getInteractionDensity() {
    const featureCount = this.getSessionFeatureCount();
    const sessionTime = Math.max(this.getEngagementTime(), 1);
    return Math.round((featureCount / sessionTime) * 100) / 100; // interactions per second
  }

  identifyBehaviorPattern(context = {}) {
    const featureCount = this.getSessionFeatureCount();
    const sessionTime = this.getEngagementTime();
    
    if (featureCount > 5 && sessionTime < 120) return 'power_user';
    if (featureCount < 2 && sessionTime > 300) return 'researcher';
    if (featureCount >= 3 && sessionTime > 180) return 'analyzer';
    if (context.hasComparisons) return 'comparison_shopper';
    return 'explorer';
  }

  // Check if analytics is available
  isAvailable() {
    return this.isInitialized && window.gtag;
  }
}

// Create singleton instance
const analytics = new AnalyticsService();

export default analytics;

// Export specific tracking methods for convenience
export const {
  trackPageView,
  trackStockEvent,
  trackFeatureUsage,
  trackEngagement,
  trackApiCall,
  trackFunnelStep,
  trackAuth,
  trackError,
  setUserProperties,
  setCustomUserProperties,
  clearUser
} = analytics;